/**
 * Industry nodes ⇄ Cloudscape `PropertyFilter` query.
 *
 * The prototype drew this section as a `Set Criteria` box producing `L1=FSI/SMB ×`
 * tokens — which is what `PropertyFilter` renders, and what the Impact and Tagged
 * pipeline pages already use. So the nodes are edited as a filter query and translated
 * here rather than through a bespoke select + input + token list.
 *
 * The translation is narrow on purpose, because a query builder is more expressive than
 * this data:
 *   - property key  = the sales-hierarchy level (`sh_l3` … `sh_l7`)
 *   - operator      = always `=`; a node is an equality on one level, nothing else
 *   - value         = the node name, free-form (`AUTO`, `GFD AM`)
 *   - operation     = irrelevant. Nodes within an industry are always OR'd by the
 *                    backend, so the caller hides the and/or control rather than
 *                    letting the UI offer a combinator the service ignores.
 *
 * Kept pure and separate from the page so both directions are testable without
 * rendering — the round trip is the part that can silently drop a node.
 */

import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import type { HierarchyLevel, HierarchyNode } from '../../client/IvtHubClient';

/**
 * The levels offered here. A subset of the Smithy `HierarchyLevel` enum.
 *
 * `sh_l7` is deliberately absent: SFDC populates sh_l3..sh_l6 on essentially every row
 * and sh_l7 on none, so offering it would only let someone configure a level that
 * silently matches zero opportunities.
 *
 * `territory` is finer than `sh_l6` — one `sh_l6` splits into many territories — so it carries
 * far more candidates than every other level combined.
 */
export const LEVELS: readonly HierarchyLevel[] = [
  'sh_l3',
  'sh_l4',
  'sh_l5',
  'sh_l6',
  'territory',
];

/**
 * One filtering property per level. `operators: ['=']` is the restriction that matters:
 * PropertyFilter would otherwise offer `!=`, `:` and `!:`, none of which a stored node
 * can express — a `!=` token would have to be silently dropped on save, which is worse
 * than never offering it.
 */
export const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = LEVELS.map(
  (level) => ({
    key: level,
    propertyLabel: level,
    groupValuesLabel: `${level} node names`,
    operators: ['='],
  }),
);

/** Nodes → query. Order is preserved so the tokens read in the stored order. */
export function nodesToQuery(nodes: readonly HierarchyNode[] | undefined): PropertyFilterProps.Query {
  return {
    // 'or' matches how the backend combines nodes. The control is hidden, but the value
    // still travels in the query object, so it should not claim 'and'.
    operation: 'or',
    tokens: (nodes ?? [])
      .filter((n) => n.level && n.name)
      .map((n) => ({ propertyKey: n.level as string, operator: '=', value: n.name as string })),
  };
}

/**
 * Query → nodes.
 *
 * Drops anything the data cannot hold, rather than coercing it:
 *   - free-text tokens (no `propertyKey`) — a node must name a level
 *   - a `propertyKey` outside {@link LEVELS} — level is interpolated into a COLUMN
 *     POSITION in the backend's generated SQL, so an unknown one must never be stored
 *   - blank values
 *   - duplicates, which the service rejects anyway
 *
 * Dropping is safe here because the caller re-renders from the returned nodes: a token
 * that cannot become a node disappears from the box immediately, rather than looking
 * accepted until save fails.
 */
export function queryToNodes(query: PropertyFilterProps.Query): HierarchyNode[] {
  const seen = new Set<string>();
  const out: HierarchyNode[] = [];
  for (const t of query.tokens ?? []) {
    const level = t.propertyKey;
    const name = typeof t.value === 'string' ? t.value.trim() : '';
    if (!level || !LEVELS.includes(level as HierarchyLevel) || name === '') continue;
    const key = `${level}|${name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ level: level as HierarchyLevel, name });
  }
  return out;
}
